import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import swal from 'sweetalert';
import './CareparrvaOutput.css';
import careparrvaOutputService from '../../services/careparrvaOutputService';
import { showToast } from '../../components/toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faSyncAlt,
  faDownload,
  faExternalLinkAlt,
  faEye,
  faTrashAlt,
  faExclamationTriangle,
  faSpinner,
  faCopy,
  faCheck,
  faInfoCircle,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';

const DATA_TYPES = [
  { value: 'LATEST_DATA', label: 'Latest Data (Current Session)' },
  { value: 'PREV_MONTH_END_DATA', label: 'Previous Month-End Data' },
];

const REPORT_TYPES = [
  { value: 'ALL', label: 'All Report Advice' },
  { value: 'PORTFOLIO', label: 'Portfolio' },
  { value: 'INTRADAY', label: 'Intraday' },
  { value: 'SINGLESTOCK', label: 'Single Stock' },
  { value: 'DERIVATIVES', label: 'Derivatives' },
  { value: 'STRATEGY', label: 'Strategy' },
  { value: 'ALGORITHM', label: 'Algorithm' },
];

const REPORT_CATEGORIES = [
  { value: 'DETAIL', label: 'Detail' },
  { value: 'SUMMARY', label: 'Summary' },
];

const REPORT_FORMATS = [
  { value: 'PDF', label: 'PDF Document' },
  { value: 'PNG', label: 'PNG Image' },
  { value: 'JPEG', label: 'JPEG Image' },
  { value: 'QR', label: 'QR Code' },
  { value: 'URL', label: 'Direct Web URL' },
  { value: 'DATA_POINTS', label: 'Raw Data Points (JSON)' },
];

const CareparrvaOutput = () => {
  const navigate = useNavigate();

  // Table state
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(15);
  const [totalCount, setTotalCount] = useState(0);

  // Filter state
  const [filterFormat, setFilterFormat] = useState('ALL');
  const [filterType, setFilterType] = useState('ALL');

  // Create Report Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    data_type: 'LATEST_DATA',
    report_type: 'PORTFOLIO',
    report_category: 'DETAIL',
    report_format: 'DATA_POINTS',
    is_historical: 'FALSE',
  });
  const [generating, setGenerating] = useState(false);
  const [modalError, setModalError] = useState(null);

  // Syncing state for historical reports
  const [syncingId, setSyncingId] = useState(null);
  const [syncingAll, setSyncingAll] = useState(false);

  // Load reports on mount and when page changes
  useEffect(() => {
    loadReports();
  }, [page]);

  const loadReports = async () => {
    try {
      setLoading(true);
      const res = await careparrvaOutputService.getReports({ page, limit });
      const list = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res)
        ? res
        : [];
      const total =
        typeof res?.total === 'number'
          ? res.total
          : Array.isArray(res?.data)
          ? res.data.length
          : Array.isArray(res)
          ? res.length
          : 0;

      if (res && (res.success || Array.isArray(res.data) || Array.isArray(res))) {
        setReports(list);
        setTotalCount(total);
      } else {
        setReports([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('Error fetching reports:', err);
      showToast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  // Open Create Modal
  const handleOpenCreateModal = () => {
    setModalError(null);
    setShowCreateModal(true);
  };

  // Close Create Modal
  const handleCloseCreateModal = () => {
    if (!generating) {
      setShowCreateModal(false);
      setModalError(null);
    }
  };

  // Input change
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (modalError) {
      setModalError(null);
    }
  };

  // Helper to extract proper human-readable error messages from nested backend/Careparrva responses
  const extractErrorMessage = (err, fallback = 'Failed to generate report.') => {
    const data = err?.response?.data || err;

    // 1. Check rawResponse.messages or messages array from Careparrva
    const messages = data?.rawResponse?.messages || data?.messages;
    if (Array.isArray(messages) && messages.length > 0) {
      const details = messages
        .map((m) => {
          const valText = Array.isArray(m?.vals) && m.vals.length > 0 ? m.vals.join(', ') : '';
          const field = m?.field ? `[${m.field}] ` : '';
          return valText ? `${field}${valText}` : m?.message || m?.errcode || '';
        })
        .filter(Boolean);
      if (details.length > 0) {
        return details.join(' | ');
      }
    }

    // 2. Check backend message string or array
    if (data?.message) {
      if (Array.isArray(data.message)) {
        return data.message.join(', ');
      }
      if (
        typeof data.message === 'string' &&
        data.message.toLowerCase() !== 'invalid' &&
        data.message.toLowerCase() !== 'error'
      ) {
        return data.message;
      }
    }

    // 3. Check error string
    if (typeof data?.error === 'string') {
      return data.error;
    }

    return err?.message || fallback;
  };

  // Handle Generate Report submission
  const handleGenerateSubmit = async (e) => {
    if (e) e.preventDefault();
    setModalError(null);
    setGenerating(true);

    try {
      const response = await careparrvaOutputService.generateReport(formData);

      if (response && response.success) {
        showToast.success('Report generated and saved successfully!');
        setShowCreateModal(false);
        setModalError(null);
        setPage(1);
        loadReports();
      } else {
        const errorMsg = extractErrorMessage(response, response?.message || 'Failed to generate report.');
        setModalError(errorMsg);
      }
    } catch (err) {
      console.error('Error generating report:', err);
      const errorMsg = extractErrorMessage(err);
      setModalError(errorMsg);
    } finally {
      setGenerating(false);
    }
  };

  // Sync / check status of a report (especially historical reports)
  const handleSyncStatus = async (reportId) => {
    try {
      setSyncingId(reportId);
      const result = await careparrvaOutputService.syncReportStatus(reportId);
      if (result && (result.success || result.report || result.id)) {
        showToast.success('Report status synced successfully!');
        loadReports();
      } else {
        showToast.warning(result?.message || 'Status sync completed with no change.');
      }
    } catch (err) {
      console.error('Error syncing report status:', err);
      const errMsg =
        err?.response?.data?.message || err?.message || 'Failed to sync status.';
      showToast.error(String(errMsg));
    } finally {
      setSyncingId(null);
    }
  };

  // Sync all pending reports in batch
  const handleSyncAllPending = async () => {
    try {
      setSyncingAll(true);
      const res = await careparrvaOutputService.syncAllPending();
      if (res && res.success) {
        showToast.info(res.message || 'Pending reports check complete');
        loadReports();
      } else {
        showToast.warning(res?.message || 'Sync completed with no updates');
      }
    } catch (err) {
      console.error('Error syncing all pending reports:', err);
      showToast.error('Failed to sync pending reports');
    } finally {
      setSyncingAll(false);
    }
  };

  // Delete a report record
  const handleDeleteReport = (reportId) => {
    swal({
      title: 'Are you sure?',
      text: 'Do you really want to delete this report record?',
      icon: 'warning',
      buttons: ['Cancel', 'Yes, Delete'],
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          const res = await careparrvaOutputService.deleteReport(reportId);
          if (res && res.success) {
            showToast.success('Report deleted successfully');
            loadReports();
          } else {
            showToast.error(res?.message || 'Failed to delete report');
          }
        } catch (err) {
          console.error('Error deleting report:', err);
          showToast.error('Error deleting report');
        }
      }
    });
  };

  // View Data Points redirect to dedicated report page
  const handleViewDataPoints = (report) => {
    if (report?.id) {
      navigate(`/research/careparrva-output/view/${report.id}`, {
        state: { report },
      });
    }
  };

  // Download raw JSON
  const handleDownloadJson = (report) => {
    if (!report?.data_points) {
      showToast.warning('No data points available to download');
      return;
    }
    try {
      const content =
        typeof report.data_points === 'string'
          ? report.data_points
          : JSON.stringify(report.data_points, null, 2);
      const blob = new Blob([content], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `careparrva_${(report.report_type || 'report').toLowerCase()}_${report.id}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast.success('JSON file downloaded');
    } catch (err) {
      console.error('Error downloading json:', err);
      showToast.error('Could not download JSON');
    }
  };

  // Helper for format badge
  const renderFormatBadge = (format) => {
    switch (format) {
      case 'PDF':
        return <span className="badge badge-danger">PDF</span>;
      case 'PNG':
      case 'JPEG':
        return <span className="badge badge-primary">{format}</span>;
      case 'QR':
        return <span className="badge text-white" style={{ backgroundColor: '#7c3aed' }}>QR</span>;
      case 'URL':
        return <span className="badge badge-info">URL</span>;
      case 'DATA_POINTS':
        return <span className="badge badge-inverse">DATA_POINTS</span>;
      default:
        return <span className="badge badge-secondary">{format}</span>;
    }
  };

  // Helper for status badge
  const renderStatusBadge = (status) => {
    switch (status) {
      case 'COMPLETED':
        return (
          <span className="badge badge-success" style={{ backgroundColor: '#10b981', color: '#ffffff' }}>
            Completed
          </span>
        );
      case 'PENDING':
        return <span className="badge badge-warning text-dark">Pending</span>;
      case 'FAILED':
        return <span className="badge badge-danger">Failed</span>;
      default:
        return <span className="badge badge-secondary">{status || 'Unknown'}</span>;
    }
  };

  // Helper to format date
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      });
    } catch {
      return dateStr;
    }
  };

  // Filtered reports
  const filteredReports = reports.filter((item) => {
    if (filterFormat !== 'ALL' && item.report_format !== filterFormat) return false;
    if (filterType !== 'ALL' && item.report_type !== filterType) return false;
    return true;
  });

  const totalPages = Math.ceil(totalCount / limit) || 1;

  return (
    <div id="page-container" className="page-header-fixed page-sidebar-fixed fade in cpo-container">
      <div id="content" className="">
        {/* Top Header Row: Breadcrumb on LEFT, Standard CRM Buttons on RIGHT */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 pb-2">
          <div>
            <ul className="breadcrumb mb-0">
              <li className="breadcrumb-item">
                <a href="/Dashboard">
                  <i className="ti-home"></i> Home
                </a>
              </li>
              <li className="breadcrumb-item">Research</li>
              <li className="breadcrumb-item active">Careparrva Output</li>
            </ul>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <button
              className="btn btn-default btn-sm"
              onClick={handleOpenCreateModal}
            >
              <FontAwesomeIcon icon={faPlus} className="me-1" /> Create Report
            </button>
            <button
              className="btn btn-default btn-sm"
              onClick={handleSyncAllPending}
              disabled={syncingAll}
              title="Check all pending reports"
            >
              <FontAwesomeIcon
                icon={faSyncAlt}
                spin={syncingAll}
                className="me-1"
              />{' '}
              Sync Pending
            </button>
            <button
              className="btn btn-default btn-sm"
              onClick={loadReports}
              disabled={loading}
              title="Refresh"
            >
              <FontAwesomeIcon
                icon={faSyncAlt}
                spin={loading}
                className="me-1"
              />{' '}
              Refresh
            </button>
          </div>
        </div>

        {/* Panel Container matching StreetGains CRM */}
        <div className="panel panel-default with-rounded-corner with-shadow p-10">
          {/* Header & Filter Bar inside Panel */}
          <div className="d-flex align-items-center justify-content-between p-3 border-bottom flex-wrap gap-2">
            <h4 className="m-0" style={{ fontSize: '15px', fontWeight: '600' }}>
              Generated Reports List
            </h4>
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div className="d-flex align-items-center gap-1">
                <span className="text-muted" style={{ fontSize: '12px', fontWeight: '600' }}>
                  Format:
                </span>
                <select
                  className="form-control input-sm"
                  style={{ width: '130px', height: '30px', fontSize: '12px', padding: '2px 8px' }}
                  value={filterFormat}
                  onChange={(e) => setFilterFormat(e.target.value)}
                >
                  <option value="ALL">All Formats</option>
                  <option value="PDF">PDF</option>
                  <option value="PNG">PNG</option>
                  <option value="JPEG">JPEG</option>
                  <option value="QR">QR</option>
                  <option value="URL">URL</option>
                  <option value="DATA_POINTS">DATA_POINTS</option>
                </select>
              </div>

              <div className="d-flex align-items-center gap-1">
                <span className="text-muted" style={{ fontSize: '12px', fontWeight: '600' }}>
                  Type:
                </span>
                <select
                  className="form-control input-sm"
                  style={{ width: '140px', height: '30px', fontSize: '12px', padding: '2px 8px' }}
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="ALL">All Types</option>
                  <option value="PORTFOLIO">Portfolio</option>
                  <option value="INTRADAY">Intraday</option>
                  <option value="SINGLESTOCK">Single Stock</option>
                  <option value="DERIVATIVES">Derivatives</option>
                  <option value="STRATEGY">Strategy</option>
                  <option value="ALGORITHM">Algorithm</option>
                </select>
              </div>

              {totalCount > 0 && (
                <div className="d-inline-block ms-2">
                  <span style={{ fontSize: '0.8rem' }} className="me-2 text-muted">
                    {(page - 1) * limit + 1} to {Math.min(page * limit, totalCount)} of {totalCount}
                  </span>
                  <button
                    className="btn btn-default btn-sm"
                    title="Previous"
                    onClick={() => setPage((p) => Math.max(p - 1, 1))}
                    disabled={page <= 1}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                  &nbsp;
                  <button
                    className="btn btn-default btn-sm"
                    title="Next"
                    onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                    disabled={page >= totalPages}
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Table matching CRM theme */}
          <div className="table-responsive">
            <table className="table table-bordered table-hover bg-white mb-0">
              <thead>
                <tr>
                  <th style={{ width: '45px', textAlign: 'center' }}>#</th>
                  <th style={{ minWidth: '150px' }}>Created On</th>
                  <th style={{ minWidth: '120px' }}>Report Type</th>
                  <th style={{ minWidth: '130px' }}>Data Type</th>
                  <th style={{ minWidth: '95px' }}>Category</th>
                  <th style={{ minWidth: '95px', textAlign: 'center' }}>Format</th>
                  <th style={{ minWidth: '90px', textAlign: 'center' }}>Historical</th>
                  <th style={{ minWidth: '100px', textAlign: 'center' }}>Status</th>
                  <th style={{ minWidth: '140px', textAlign: 'center' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && reports.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center py-4 text-muted">
                      <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                      Loading reports...
                    </td>
                  </tr>
                ) : filteredReports.length > 0 ? (
                  filteredReports.map((report, idx) => (
                    <tr key={report.id}>
                      <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                        {(page - 1) * limit + idx + 1}
                      </td>
                      <td style={{ verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                        <span style={{ fontWeight: '500' }}>
                          {formatDateTime(report.created_on)}
                        </span>
                        {report.request_id && (
                          <div className="text-muted" style={{ fontSize: '11px' }}>
                            Req ID: {report.request_id}
                          </div>
                        )}
                      </td>
                      <td style={{ verticalAlign: 'middle' }}>
                        <span className="font-weight-bold">{report.report_type}</span>
                      </td>
                      <td style={{ verticalAlign: 'middle', fontSize: '12px' }}>
                        {report.data_type === 'LATEST_DATA'
                          ? 'Latest Data'
                          : report.data_type === 'PREV_MONTH_END_DATA'
                          ? 'Prev Month-End'
                          : report.data_type}
                      </td>
                      <td style={{ verticalAlign: 'middle', fontSize: '12px' }}>
                        {report.report_category}
                      </td>
                      <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                        {renderFormatBadge(report.report_format)}
                      </td>
                      <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                        {report.is_historical === 'TRUE' ? (
                          <span className="badge badge-warning text-dark">Yes</span>
                        ) : (
                          <span className="badge badge-secondary">No</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                        {renderStatusBadge(report.status)}
                      </td>
                      <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                        <div
                          className="d-flex justify-content-center align-items-center"
                          style={{ gap: '4px' }}
                        >
                          {/* DATA_POINTS Actions: View button strictly appears only for DATA_POINTS format */}
                          {report.report_format === 'DATA_POINTS' && report.data_points && (
                            <>
                              <button
                                type="button"
                                className="btn btn-info btn-xs"
                                onClick={() => handleViewDataPoints(report)}
                                title="View Data Points"
                              >
                                <FontAwesomeIcon icon={faEye} />
                              </button>
                              <button
                                type="button"
                                className="btn btn-primary btn-xs"
                                onClick={() => handleDownloadJson(report)}
                                title="Download JSON"
                              >
                                <FontAwesomeIcon icon={faDownload} />
                              </button>
                            </>
                          )}

                          {/* File URL Actions */}
                          {report.file_url && (
                            report.report_format === 'URL' || report.report_format === 'QR' ? (
                              <a
                                href={report.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-info btn-xs"
                                title="Open Link"
                              >
                                <FontAwesomeIcon icon={faExternalLinkAlt} />
                              </a>
                            ) : (
                              <a
                                href={report.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                download
                                className="btn btn-success btn-xs"
                                title="Download File"
                              >
                                <FontAwesomeIcon icon={faDownload} />
                              </a>
                            )
                          )}

                          {/* Sync Status Button for Pending reports */}
                          {(report.status === 'PENDING' ||
                            (report.is_historical === 'TRUE' && !report.file_url)) && (
                            <button
                              type="button"
                              className="btn btn-warning btn-xs"
                              onClick={() => handleSyncStatus(report.id)}
                              disabled={syncingId === report.id}
                              title="Sync Status from Careparrva API"
                            >
                              <FontAwesomeIcon
                                icon={faSyncAlt}
                                spin={syncingId === report.id}
                              />
                            </button>
                          )}

                          {/* Delete Button */}
                          <button
                            type="button"
                            className="btn btn-danger btn-xs"
                            onClick={() => handleDeleteReport(report.id)}
                            title="Delete Record"
                          >
                            <FontAwesomeIcon icon={faTrashAlt} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center py-4 text-muted">
                      No reports found. Click <strong>"Create Report"</strong> on top-right to generate a new report.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalCount > limit && (
            <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
              <span className="text-muted" style={{ fontSize: '13px' }}>
                Showing {(page - 1) * limit + 1} to{' '}
                {Math.min(page * limit, totalCount)} of {totalCount} records
              </span>
              <div>
                <button
                  className="btn btn-sm btn-default me-1"
                  onClick={() => setPage((p) => Math.max(p - 1, 1))}
                  disabled={page <= 1}
                >
                  <FontAwesomeIcon icon={faChevronLeft} className="me-1" /> Previous
                </button>
                <span className="mx-2 text-muted" style={{ fontSize: '13px' }}>
                  Page {page} of {totalPages}
                </span>
                <button
                  className="btn btn-sm btn-default ms-1"
                  onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                  disabled={page >= totalPages}
                >
                  Next <FontAwesomeIcon icon={faChevronRight} className="ms-1" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ============================================================== */}
      {/* MODAL: CREATE / GENERATE REPORT                                 */}
      {/* ============================================================== */}
      <Modal show={showCreateModal} onHide={handleCloseCreateModal} size="lg" className="cpo-modal">
        <form onSubmit={handleGenerateSubmit}>
          <Modal.Header closeButton={!generating}>
            <h5 className="modal-title font-weight-bold">
              Create Careparrva Output Report
            </h5>
          </Modal.Header>
          <Modal.Body>
            <div className="row">
              {/* Data Type */}
              <div className="col-md-6 form-group">
                <label className="font-weight-bold">
                  Data Type <span className="text-danger">*</span>
                </label>
                <select
                  className="form-control"
                  value={formData.data_type}
                  onChange={(e) => handleInputChange('data_type', e.target.value)}
                  disabled={generating}
                >
                  {DATA_TYPES.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
                <small className="form-text text-muted">
                  Choose current session data or previous month-end data
                </small>
              </div>

              {/* Report Type */}
              <div className="col-md-6 form-group">
                <label className="font-weight-bold">
                  Report Type <span className="text-danger">*</span>
                </label>
                <select
                  className="form-control"
                  value={formData.report_type}
                  onChange={(e) => handleInputChange('report_type', e.target.value)}
                  disabled={generating}
                >
                  {REPORT_TYPES.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
                <small className="form-text text-muted">
                  Research report domain (Portfolio, Intraday, etc.)
                </small>
              </div>

              {/* Report Category */}
              <div className="col-md-6 form-group">
                <label className="font-weight-bold">
                  Report Category <span className="text-danger">*</span>
                </label>
                <select
                  className="form-control"
                  value={formData.report_category}
                  onChange={(e) => handleInputChange('report_category', e.target.value)}
                  disabled={generating}
                >
                  {REPORT_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
                <small className="form-text text-muted">
                  Comprehensive Detail or concise Summary
                </small>
              </div>

              {/* Report Format */}
              <div className="col-md-6 form-group">
                <label className="font-weight-bold">
                  Report Format <span className="text-danger">*</span>
                </label>
                <select
                  className="form-control"
                  value={formData.report_format}
                  onChange={(e) => handleInputChange('report_format', e.target.value)}
                  disabled={generating}
                >
                  {REPORT_FORMATS.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
                <small className="form-text text-muted">
                  {formData.report_format === 'PDF' ||
                  formData.report_format === 'PNG' ||
                  formData.report_format === 'JPEG'
                    ? 'Uploaded directly to S3 and stored in database'
                    : formData.report_format === 'DATA_POINTS'
                    ? 'Stored as raw JSON in database'
                    : 'Stored directly as URL in database'}
                </small>
              </div>

              {/* Historical Flag */}
              <div className="col-md-12 form-group">
                <label className="font-weight-bold" style={{ marginBottom: '8px', display: 'block' }}>
                  Historical Data Mode
                </label>
                <div className="cpo-radio-group">
                  <label className="cpo-radio-item">
                    <input
                      type="radio"
                      name="is_historical"
                      value="FALSE"
                      checked={formData.is_historical === 'FALSE'}
                      onChange={() => handleInputChange('is_historical', 'FALSE')}
                      disabled={generating}
                    />
                    <span>No (Live / Current Data)</span>
                  </label>
                  <label className="cpo-radio-item">
                    <input
                      type="radio"
                      name="is_historical"
                      value="TRUE"
                      checked={formData.is_historical === 'TRUE'}
                      onChange={() => handleInputChange('is_historical', 'TRUE')}
                      disabled={generating}
                    />
                    <span>Yes (Historical Data Request)</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Context Notice for Historical reports */}
            {formData.is_historical === 'TRUE' && (
              <div className="alert alert-info py-2 px-3 mt-1 mb-2" style={{ fontSize: '13px' }}>
                <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
                Historical data requests are queued by Careparrva. Once submitted, you can monitor and sync the completed report from the table.
              </div>
            )}

            {/* ERROR DISPLAY AT BOTTOM OF MODAL */}
            {modalError && (
              <div
                className="alert alert-danger cpo-alert-danger mt-3 mb-0"
                style={{
                  borderRadius: '6px',
                  fontSize: '13px',
                  borderLeft: '4px solid #dc3545',
                }}
              >
                <div className="d-flex align-items-start">
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    className="mr-2 mt-1 text-danger"
                  />
                  <div style={{ flex: 1 }}>
                    <strong className="text-danger">Generation Error:</strong>
                    <div style={{ wordBreak: 'break-word', marginTop: '4px' }}>
                      {modalError}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button
              type="submit"
              className="btn btn-primary submitBtn"
              disabled={generating}
            >
              {generating ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin className="mr-1" /> Generating...
                </>
              ) : (
                'Generate & Save'
              )}
            </button>
            <button
              type="button"
              className="btn btn-default"
              onClick={handleCloseCreateModal}
              disabled={generating}
            >
              Cancel
            </button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
};

export default CareparrvaOutput;
